import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, MapPin, FileSpreadsheet, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay: d, ease: 'easeOut' },
});

export const Hero = ({ onDemo }) => (
  <section id="top" className="pt-28 sm:pt-36 pb-16 sm:pb-24 overflow-hidden" data-testid="hero-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-8 items-center">
      <div className="text-left">
        <motion.div {...fade(0)} className="inline-flex items-center gap-2 rounded-full bg-terracotta/10 border border-terracotta/25 px-4 py-1.5 mb-6">
          <ShieldCheck className="w-4 h-4 text-terracotta" />
          <span className="font-body-landing text-xs sm:text-sm font-semibold text-forest-900">Nueva normativa 2026: el fichaje digital será obligatorio</span>
        </motion.div>
        <motion.h1 {...fade(0.08)} className="font-display font-black text-forest-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6">
          El control horario de tu empresa, <span className="text-terracotta">resuelto en 5 minutos</span>
        </motion.h1>
        <motion.p {...fade(0.16)} className="font-body-landing text-forest-700 text-base md:text-lg leading-relaxed mb-8 max-w-xl">
          Fichajes en tablet, ordenador o móvil con GPS y biometría.
          Autocierre inteligente que el empleado valida, informes profesionales
          que se envían solos a tu gestoría y cuadrantes de turnos en 2 clicks.
          <strong className="text-forest-900"> Cumples la ley 2026 desde el día 1</strong> y ganas horas a la semana.
        </motion.p>
        <motion.div {...fade(0.24)} className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8">
          <Button asChild size="lg" className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white font-body-landing font-bold h-13 h-12 px-8 text-base group">
            <Link to="/registro" data-testid="hero-trial-btn">
              Empieza gratis 15 días
              <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
          <Button size="lg" variant="outline" onClick={onDemo} className="rounded-full border-forest-900/20 text-forest-900 hover:bg-forest-100 font-body-landing font-bold h-12 px-8 text-base bg-transparent" data-testid="hero-demo-btn">
            Ver demo · Te llamamos
          </Button>
        </motion.div>
        <motion.ul {...fade(0.32)} className="flex flex-wrap gap-x-6 gap-y-2 font-body-landing text-sm text-forest-700">
          {['Sin tarjeta de crédito', 'Todo incluido desde el primer día', 'Cancela cuando quieras'].map((t) => (
            <li key={t} className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-terracotta" />{t}
            </li>
          ))}
        </motion.ul>
      </div>

      <motion.div {...fade(0.2)} className="relative lg:pl-6" data-testid="hero-visual">
        <div className="relative rounded-3xl border border-forest-900/10 bg-white shadow-[0_24px_60px_rgb(15,28,20,0.12)] overflow-hidden rotate-1">
          <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-forest-900/5 bg-bone">
            <span className="w-2.5 h-2.5 rounded-full bg-terracotta/60" />
            <span className="w-2.5 h-2.5 rounded-full bg-forest-100" />
            <span className="w-2.5 h-2.5 rounded-full bg-forest-100" />
          </div>
          <img src="/landing/admin_dashboard.png" alt="Panel de administración de GestionaEmpleados" className="w-full block" loading="eager" />
        </div>
        <div className="absolute -bottom-8 -left-2 sm:-left-8 w-36 sm:w-44 rounded-[1.6rem] border-4 border-forest-900 shadow-[0_20px_50px_rgb(15,28,20,0.35)] overflow-hidden -rotate-3 bg-forest-900">
          <img src="/landing/app_empleado.png" alt="App móvil del empleado con fichaje GPS" className="w-full block" />
        </div>
        <div className="absolute -top-5 -right-2 sm:-right-4 bg-white rounded-2xl border border-forest-900/10 shadow-lg px-4 py-3 flex items-center gap-3 rotate-2">
          <MapPin className="w-5 h-5 text-terracotta" />
          <div className="font-body-landing text-left">
            <p className="text-xs font-bold text-forest-900">Fichaje con GPS</p>
            <p className="text-[11px] text-forest-700">Dentro del centro de trabajo</p>
          </div>
        </div>
        <div className="absolute bottom-10 -right-2 sm:-right-6 bg-forest-900 text-bone rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3 -rotate-1">
          <FileSpreadsheet className="w-5 h-5 text-terracotta" />
          <p className="font-body-landing text-xs font-bold">Informes automáticos<br /><span className="font-normal opacity-70">directos a tu gestoría</span></p>
        </div>
      </motion.div>
    </div>
  </section>
);
